// react
import React, { useState, useEffect } from "react";

// context
import { UserAuth } from "context/AuthContext";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import {
  useMediaQuery,
  Button,
  Grid,
  SwipeableDrawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
} from "@mui/material";
import { CloseOutlined as CloseOutlinedIcon, Verified as VerifiedIcon } from "@mui/icons-material";

// ui-components
import { LinearLoadingComponent } from "ui-components/LoadingComponent";
import { Puller } from "ui-components/Puller";
import { NewCertConfirm } from "ui-components/ORFeedbacks";

// SafeTwin
import { decryptSeedByPasswordHash4Ed25519 } from "SafeTwin/crypto/cryptoseed";

// ConfigGenerator
import genConfigRecord from "generator/ConfigGenerator/genConfigRecord";

// A ---------------------------------------------------------------------- M

const getButtonStyle = (action) => ({
  py: 0.5,
  borderColor: "divider",
  color: action === "close" ? "error.main" : "primary.main",
  textTransform: "none",
  boxShadow: "none",
  borderRadius: 10,
  "&:hover": {
    borderColor: action === "close" ? "error.dark" : "primary.dark",
    backgroundColor: "action.hover",
  },
});

const ConfigGenerator = ({ tag, targets, types, setTypes, open, setOpen, handleOpenCertificationSuccessful, handleOpenCertificationError }) => {
  const { user } = UserAuth();
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [keypair, setKeyPair] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState("");
  const [targetPrototype, setTargetPrototype] = useState("");
  const [targetPrototypeMeta, setTargetPrototypeMeta] = useState("");
  const [propertyValues, setPropertyValues] = useState({});
  const [targetSelectionOpen, setTargetSelectionOpen] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handlePopstate = () => {
      if (targetSelectionOpen) handleCloseTargetSelection();
      else if (open) handleReset();
    };
    window.addEventListener("popstate", handlePopstate);
    return () => window.removeEventListener("popstate", handlePopstate);
  }, [open, targetSelectionOpen]);

  useEffect(() => {
    const decrypt = () => {
      if (open) {
        const keypair = decryptSeedByPasswordHash4Ed25519(user.reloadUserInfo.passwordHash);
        setKeyPair(keypair);
      }
    };

    decrypt();
  }, [open]);

  useEffect(() => {
    setPropertyValues({});
  }, [selectedTarget]);

  const handleOpenTargetSelection = () => {
    setTargetSelectionOpen(true);
    window.history.pushState(null, "");
  };

  const handleCloseTargetSelection = () => {
    setTargetSelectionOpen(false);
  };

  const handleGenerate = async () => {
    setConfirm(false);
    setLoading(true);

    const configObject = {};
    Object.entries(propertyValues).forEach(([key, value]) => {
      configObject[key] = value;
    });

    const jsonconfig = JSON.stringify(configObject);

    if (keypair) {
      try {
        let result = null;
        const dataBody = {
          target: selectedTarget,
          jsonconfig,
        };

        if (tag) {
          result = await genConfigRecord(isPublic, user.uid, keypair, dataBody, tag);
        }

        if (result.success) {
          handleOpenCertificationSuccessful();

          if (!types.includes("cfg")) {
            setTypes([...types, "cfg"]);
          }
        } else {
          handleOpenCertificationError();
        }
      } catch (error) {
        console.error("Error in handleGenerate:", error.message);
        handleOpenCertificationError();
      } finally {
        setLoading(false);
        handleReset();
      }
    } else {
      console.log("No keypair provided.");
    }

    setLoading(false);
    handleReset();
  };

  const handleReset = () => {
    setSelectedTarget("");
    setTargetPrototype("");
    setPropertyValues({});
    setOpen(false);
    setIsPublic(false);
  };

  const handleTargetChange = (event) => {
    const selectedTargetID = event.target.value;
    const selectedTarget = targets.find((target) => target.name === selectedTargetID);

    if (selectedTarget) {
      setSelectedTarget(selectedTargetID);
      setTargetPrototype(selectedTarget.prototype);

      if (selectedTarget.prototype_meta) {
        setTargetPrototypeMeta(selectedTarget.prototype_meta);
      }

      const initialValues = {};

      if (selectedTarget.prototype) {
        selectedTarget.prototype.split(";").forEach((property) => {
          initialValues[property] = "";
        });
      }

      setPropertyValues(initialValues);
    } else {
      setSelectedTarget("");
      setTargetPrototype("");
      setPropertyValues({});
    }
  };

  const DialogTitleContent = () => (
    <Grid container alignItems="center" justifyContent="space-between">
      <Grid item>
        <Typography variant="h6" fontWeight="bold">
          {t("certify_configuration")}
        </Typography>
      </Grid>
      <Grid item>
        <Tooltip title={t("close")} placement="top">
          <IconButton onClick={handleReset} color="error" edge="end">
            <CloseOutlinedIcon />
          </IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  );

  const GeneratorForm = (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="body2" color="gray">
          {t("select_target")}
        </Typography>
        <FormControl fullWidth>
          <Select id="target-select" value={selectedTarget} onChange={handleTargetChange} open={targetSelectionOpen} onOpen={handleOpenTargetSelection} onClose={handleCloseTargetSelection}>
            {targets.map((target) => (
              <MenuItem key={target.name} value={target.name}>
                {target.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      {targetPrototype && (
        <Grid item container xs={12} spacing={2}>
          {targetPrototype.split(";").map((property, index) => (
            <Grid item xs={12} key={index}>
              <Typography variant="body2" color="gray">{`${property} *`}</Typography>
              {targetPrototypeMeta === "select" ? (
                <Select
                  variant="outlined"
                  fullWidth
                  value={propertyValues[property] || ""}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setPropertyValues((prevValues) => ({
                      ...prevValues,
                      [property]: newValue,
                    }));
                  }}
                >
                  <MenuItem value="ON">ON</MenuItem>
                  <MenuItem value="OFF">OFF</MenuItem>
                </Select>
              ) : (
                <TextField
                  margin="dense"
                  variant="outlined"
                  fullWidth
                  value={propertyValues[property] || ""}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setPropertyValues((prevValues) => ({
                      ...prevValues,
                      [property]: newValue,
                    }));
                  }}
                />
              )}
            </Grid>
          ))}
        </Grid>
      )}
    </Grid>
  );

  return isMobile ? (
    <>
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={handleReset}
        onOpen={() => setOpen(true)}
        sx={{
          "& .MuiDrawer-paper": {
            width: "100%",
            height: "70%",
            borderTopLeftRadius: "4%",
            borderTopRightRadius: "4%",
          },
        }}
      >
        <Puller />
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold" textAlign="center" mt={2} mb={3}>
            {t("certify_configuration")}
          </Typography>
          {loading ? (
            <LinearLoadingComponent />
          ) : (
            <>
              {GeneratorForm}
              <Grid container spacing={1} mt={5}>
                <Grid item xs={12}>
                  <Button fullWidth variant="outlined" sx={getButtonStyle("close")} onClick={handleReset}>
                    {t("cancel")}
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Button fullWidth variant="contained" disabled={!selectedTarget} onClick={() => setConfirm(true)} startIcon={<VerifiedIcon />}>
                    {t("certify")}
                  </Button>
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      </SwipeableDrawer>
      <NewCertConfirm confirm={confirm} setConfirm={setConfirm} handleGenerate={handleGenerate} />
    </>
  ) : (
    <>
      <Dialog
        open={open}
        onClose={handleReset}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiPaper-root": {
            borderRadius: 2,
            p: 2,
            boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.2)",
          },
        }}
      >
        <DialogTitle>
          <DialogTitleContent />
        </DialogTitle>
        {loading ? (
          <LinearLoadingComponent />
        ) : (
          <>
            <DialogContent>{GeneratorForm}</DialogContent>
            <DialogActions>
              <Button variant="outlined" sx={getButtonStyle("close")} onClick={handleReset}>
                {t("cancel")}
              </Button>
              <Button variant="outlined" sx={getButtonStyle("open")} disabled={!selectedTarget} onClick={() => setConfirm(true)}>
                {t("certify")}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      <NewCertConfirm confirm={confirm} setConfirm={setConfirm} handleGenerate={handleGenerate} />
    </>
  );
};

export default ConfigGenerator;
